import { atom, getDefaultStore, PrimitiveAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import type { DaysmartAuthState } from "./daysmart/api";
import type { RecusAuthState } from "./recus/api";

export const atomStore = getDefaultStore();

export type VendorId = "daysmart" | "recus";
export const VENDOR_IDS: VendorId[] = ["daysmart", "recus"];

export const VENDOR_LABEL: Record<VendorId, string> = {
  daysmart: "daysmart",
  recus: "rec.us",
};

export type VendorCreds = {
  username: string;
  password: string;
  attemptLogin: boolean;
};

export type VendorProfile = {
  firstName: string;
  lastName: string;
  email: string;
};

export type VendorAccount = {
  creds: VendorCreds;
  profile?: VendorProfile;
  selectedMemberId?: string | null;
};

export type VendorAccountsMap = Partial<Record<VendorId, VendorAccount>>;

export type VendorAuthState = {
  user: VendorProfile;
  authedAt: Date;
  expiresAt: Date;
};

export type Rink = { id: string; name: string; vendor: VendorId };

export const DAYSMART_RINKS: Rink[] = [
  { id: "oakland", name: "oakland ice", vendor: "daysmart" },
  { id: "fremont", name: "sharks ice fremont", vendor: "daysmart" },
];

export const RECUS_RINKS: Rink[] = [
  { id: "yerba-buena", name: "yerba buena SF", vendor: "recus" },
];

export const ALL_RINKS: Rink[] = [...RECUS_RINKS, ...DAYSMART_RINKS];

export function getRinks(vendor: VendorId): Rink[] {
  return vendor === "daysmart" ? DAYSMART_RINKS : RECUS_RINKS;
}

export function findRink(rinkId: string): Rink | undefined {
  return ALL_RINKS.find((r) => r.id === rinkId);
}

export type ActiveSelection = { vendor: VendorId; rinkId: string };

export const activeSelectionAtom = atomWithStorage<ActiveSelection | null>(
  "activeSelection",
  null,
  undefined,
  { unstable_getOnInit: true }
);

export const activeVendorAtom = atom<VendorId | null>((get) => {
  return get(activeSelectionAtom)?.vendor ?? null;
});

export const vendorAccountsAtom = atomWithStorage<VendorAccountsMap>(
  "vendorAccounts",
  {},
  undefined,
  { unstable_getOnInit: true }
);

export const daysmartAuthStateAtom = atom<DaysmartAuthState | null>(null);
export const recusAuthStateAtom = atom<RecusAuthState | null>(null);

export const vendorAuthStateAtoms: Record<
  VendorId,
  PrimitiveAtom<VendorAuthState | null>
> = {
  daysmart: daysmartAuthStateAtom as PrimitiveAtom<VendorAuthState | null>,
  recus: recusAuthStateAtom as PrimitiveAtom<VendorAuthState | null>,
};

export const activeVendorAuthStateAtom = atom<VendorAuthState | null>((get) => {
  const vendor = get(activeVendorAtom);
  if (!vendor) return null;
  return get(vendorAuthStateAtoms[vendor]);
});

export const enrolledVendorsAtom = atom<VendorId[]>((get) => {
  const accounts = get(vendorAccountsAtom);
  return VENDOR_IDS.filter((v) => accounts[v]);
});

export function getVendorAccount(vendor: VendorId): VendorAccount | null {
  return atomStore.get(vendorAccountsAtom)[vendor] ?? null;
}

export function getVendorCreds(vendor: VendorId): VendorCreds | null {
  return getVendorAccount(vendor)?.creds ?? null;
}

export function updateVendorCreds(
  vendor: VendorId,
  patch: Partial<VendorCreds>
): void {
  const current = atomStore.get(vendorAccountsAtom);
  const existing = current[vendor];
  const existingCreds: VendorCreds = existing?.creds ?? {
    username: "",
    password: "",
    attemptLogin: false,
  };
  atomStore.set(vendorAccountsAtom, {
    ...current,
    [vendor]: {
      ...(existing ?? {}),
      creds: { ...existingCreds, ...patch },
    },
  });
}

export function clearVendorCreds(vendor: VendorId): void {
  const current = { ...atomStore.get(vendorAccountsAtom) };
  delete current[vendor];
  atomStore.set(vendorAccountsAtom, current);
  atomStore.set(vendorAuthStateAtoms[vendor], null);

  const sel = atomStore.get(activeSelectionAtom);
  if (sel?.vendor === vendor) {
    const remaining = VENDOR_IDS.filter(
      (v) => atomStore.get(vendorAccountsAtom)[v]
    );
    if (remaining.length > 0) {
      setActiveSelection(remaining[0]);
    } else {
      atomStore.set(activeSelectionAtom, null);
    }
  }
}

export function setVendorProfile(
  vendor: VendorId,
  profile: VendorProfile
): void {
  const current = atomStore.get(vendorAccountsAtom);
  const existing = current[vendor];
  if (!existing) return;
  atomStore.set(vendorAccountsAtom, {
    ...current,
    [vendor]: { ...existing, profile },
  });
}

export function setSelectedMemberId(
  vendor: VendorId,
  id: string | null
): void {
  const current = atomStore.get(vendorAccountsAtom);
  const existing = current[vendor];
  if (!existing) return;
  atomStore.set(vendorAccountsAtom, {
    ...current,
    [vendor]: { ...existing, selectedMemberId: id },
  });
}

export function firstEnrolledVendor(): VendorId | null {
  const accounts = atomStore.get(vendorAccountsAtom);
  return VENDOR_IDS.find((v) => accounts[v]) ?? null;
}

export function setActiveSelection(vendor: VendorId, rinkId?: string): void {
  const rink = rinkId ?? getRinks(vendor)[0].id;
  atomStore.set(activeSelectionAtom, { vendor, rinkId: rink });
}

export function setSelectedRink(rinkId: string): void {
  const rink = findRink(rinkId);
  if (!rink) return;
  const current = atomStore.get(activeSelectionAtom);
  if (current?.vendor !== rink.vendor) return;
  atomStore.set(activeSelectionAtom, { vendor: rink.vendor, rinkId });
}
